import { PropertyManagementSystem } from "shared/types/partner";
import { type Partner } from "types/landlord/partner";

type PmsCheckPartner = Pick<
  Partner,
  "integration_credentials" | "property_management_system"
>;

type PmsActiveArgs = {
  partner: PmsCheckPartner | null | undefined;
  pms: PropertyManagementSystem;
  isMultiPMS?: boolean;
};

type AnyPmsActiveArgs = {
  partner: PmsCheckPartner | null | undefined;
  pmsList: readonly PropertyManagementSystem[];
  isMultiPMS: boolean;
};

type BuildPmsFlagsArgs = {
  partner?: PmsCheckPartner | null;
  isMultiPMS?: boolean;
};

const isPmsActive = ({ partner, pms, isMultiPMS }: PmsActiveArgs): boolean => {
  if (isMultiPMS)
    return (
      partner?.integration_credentials?.some((c) => c.integration === pms) ??
      false
    );

  return partner?.property_management_system?.name === pms;
};

export const isAnyPmsActive = ({
  partner,
  pmsList,
  isMultiPMS
}: AnyPmsActiveArgs): boolean =>
  pmsList.some((pms) => isPmsActive({ partner, pms, isMultiPMS }));

type ActivePmsKey = Exclude<keyof typeof PropertyManagementSystem, "Other">;

export type PmsFlags = {
  [K in ActivePmsKey as `is${K}`]: boolean;
};

const ACTIVE_PMS_KEYS = (
  Object.keys(PropertyManagementSystem) as Array<
    keyof typeof PropertyManagementSystem
  >
).filter((k): k is ActivePmsKey => k !== "Other");

export const buildPmsFlags = ({
  partner,
  isMultiPMS
}: BuildPmsFlagsArgs): PmsFlags =>
  ACTIVE_PMS_KEYS.reduce((acc, key) => {
    acc[`is${key}`] = isPmsActive({
      partner,
      pms: PropertyManagementSystem[key],
      isMultiPMS
    });
    return acc;
  }, {} as PmsFlags);
